import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MY_ACCESS_API } from '../config/API';
import { isAuthenticated } from '../components/utils/auth';

const UserContext = createContext();

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

export const UserProvider = ({ children }) => {
    const [userAccess, setUserAccess] = useState({
        menus: [],
        permissions: [],
        role: null,
        userProfile: null,
        loading: true,
        error: null
    });

    const fetchUserAccess = useCallback(async () => {
        if (!isAuthenticated()) {
            setUserAccess({
                menus: [],
                permissions: [],
                role: null,
                userProfile: null,
                loading: false,
                error: null
            });
            return;
        }

        try {
            setUserAccess(prev => ({ ...prev, loading: true, error: null }));
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(MY_ACCESS_API, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });


            if (response.data.status) {
                const storedUser = localStorage.getItem('user');
                const userProfile = storedUser ? JSON.parse(storedUser) : null;

                // Robustly filter out any null values from menus and permissions
                const menus = (response.data.menus || []).filter(m => m !== null && m !== undefined);
                const permissions = (response.data.permissions || []).filter(p => p !== null && p !== undefined);

                setUserAccess({
                    menus,
                    permissions,
                    role: response.data.role,
                    userProfile,
                    loading: false,
                    error: null
                });

            } else {
                setUserAccess(prev => ({
                    ...prev,
                    loading: false,
                    error: 'Failed to fetch user access'
                }));
            }

        } catch (err) {
            console.error('Error fetching user access:', err);
            setUserAccess(prev => ({
                ...prev,
                loading: false,
                error: err.message || 'An error occurred while fetching user access'
            }));
        }
    }, []);

    const hasPermission = useCallback((menuConstant, action = 'read') => {
        if (!menuConstant) return true;

        // Super Admin bypass
        if (userAccess.role === 'SUPER_ADMIN' || userAccess.userProfile?.role === 'SUPER_ADMIN') {
            return true;
        }

        if (!userAccess.menus || !userAccess.permissions) {
            return false;
        }

        const menu = userAccess.menus.find(m => m && m.menu_constant === menuConstant);
        if (!menu) {
            return false;
        }

        const permission = userAccess.permissions.find(p =>
            p &&
            String(p.menu_id) === String(menu.id) &&
            (!p.employee_id || String(p.employee_id) === String(userAccess.userProfile?.id))
        );

        if (!permission) return false;

        let result = false;
        switch (action) {
            case 'create': result = !!permission.can_create; break;
            case 'read': result = !!permission.can_read; break;
            case 'update': result = !!permission.can_update; break;
            case 'delete': result = !!permission.can_delete; break;
            default: result = false;
        }

        // Debug logging to help identify why permissions are not working as expected
        if (menuConstant === 'INWARD_MANAGEMENT' && action === 'update') {
            console.log(`Permission check for ${menuConstant} [${action}]:`, {
                result,
                can_update: permission.can_update,
                role: userAccess.role,
                menuId: menu.id,
                permissionMenuId: permission.menu_id,
                employeeId: permission.employee_id,
                userProfileId: userAccess.userProfile?.id
            });
        }

        return result;
    }, [userAccess.menus, userAccess.permissions, userAccess.role, userAccess.userProfile]);

    useEffect(() => {
        fetchUserAccess();
    }, [fetchUserAccess]);

    const refreshUserAccess = () => {
        fetchUserAccess();
    };

    return (
        <UserContext.Provider value={{ ...userAccess, refreshUserAccess, hasPermission }}>
            {children}
        </UserContext.Provider>
    );
};
